<script setup lang="ts">
import type { AuthAuthRegisterByEmailRouteRpc } from '@repo/contract';
import { RecaptchaV2 } from 'vue3-recaptcha-v2';
import { computed, onMounted, ref, watch } from 'vue';
import { navigateTo, useMediaQuery } from '#imports';
import StageIndicator from '~/pages/registration/components/stage-indicator.vue';
import CompanyRegistrationStage from '~/pages/registration/components/stages/company-registration-stage.vue';
import ProfileRegistrationStage from '~/pages/registration/components/stages/profile-registration-stage.vue';
import { useRegistrationValidation } from '~/pages/registration/composables/use-registration-validation';
import BaseButton from '~/shared/components/buttons/base-button.vue';
import BaseClickableText from '~/shared/components/buttons/base-clickable-text.vue';

export type RegistrationStage = 'profile' | 'company' | 'captcha';

export interface RegistrationProfileData {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  passwordConfirmation: string
  acceptTerms: boolean
}

const profileData = ref<RegistrationProfileData>({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  passwordConfirmation: '',
  acceptTerms: false,
});

export type RegistrationProfileErrors = Record<keyof RegistrationProfileData, string>;

export interface CompanyProfileData {
  name: string
  url: string
  sellsPerMonth: number
  shopUrls: string[]
}

const companyData = ref<CompanyProfileData>({
  name: '',
  url: '',
  sellsPerMonth: 0,
  shopUrls: [] as string[],
});

export interface CaptchaData {
  token: string
}

const captchaData = ref<CaptchaData>({
  token: '',
});

export type RegistrationCompanyErrors = Record<keyof CompanyProfileData, string>;

const isLargeScreen = useMediaQuery('(min-width: 768px)');

const stages = ['profile', 'company', 'captcha'];
const activeStageIndex = ref(0);

const activeStage = computed(() => stages[activeStageIndex.value] as 'profile' | 'company' | 'captcha');

const stageAnimations = ref({
  profile: { classes: 'translate-x--30' },
  company: { classes: 'translate-x-30' },
  captcha: { classes: 'translate-x-30' },
});

function setAnimationLeftToRight(stage: 'profile' | 'company' | 'captcha') {
  stageAnimations.value[stage] = {
    classes: 'translate-x--30',
  };
}

function setAnimationRightToLeft(stage: 'profile' | 'company' | 'captcha') {
  stageAnimations.value[stage] = {
    classes: 'translate-x-30',
  };
}

const profileStageRef = ref<HTMLElement | null>();
const companyStageRef = ref<HTMLElement | null>();
const captchaStageRef = ref<HTMLElement | null>();

const stageContainerHeight = ref(0);

function setStageContainerHeight(stage: 'profile' | 'company' | 'captcha') {
  if (isLargeScreen.value) {
    return;
  }

  if (stage === 'profile') {
    if (profileStageRef.value) {
      stageContainerHeight.value = profileStageRef.value?.clientHeight ?? 0;
    }
  }

  if (stage === 'company') {
    if (companyStageRef.value) {
      stageContainerHeight.value = companyStageRef.value?.clientHeight ?? 0;
    }
  }

  if (stage === 'captcha') {
    if (captchaStageRef.value) {
      stageContainerHeight.value = captchaStageRef.value?.clientHeight ?? 0;
    }
  }
}

watch(companyStageRef, () => {
  if (companyStageRef.value && !isLargeScreen.value) {
    stageContainerHeight.value = companyStageRef.value?.clientHeight ?? 0;
  }
});

watch(profileStageRef, () => {
  if (profileStageRef.value && !isLargeScreen.value) {
    stageContainerHeight.value = profileStageRef.value?.clientHeight ?? 0;
  }
});

watch(captchaStageRef, () => {
  if (captchaStageRef.value && !isLargeScreen.value) {
    stageContainerHeight.value = captchaStageRef.value?.clientHeight ?? 0;
  }
});

onMounted(() => {
  setStageContainerHeight('profile');
});

const {
  profileErrors,
  companyErrors,
  trigger: triggerValidation,
  reset: resetValidation,
  captchaHasErrors,
} = useRegistrationValidation(activeStage, { profileData, companyData, captchaData });

const stageWrapperRef = ref<HTMLElement | null>();

async function nextStage() {
  const isValid = await triggerValidation();

  if (!isValid) {
    return;
  }

  if (activeStageIndex.value < stages.length - 1) {
    setAnimationRightToLeft(stages[activeStageIndex.value + 1] as 'profile' | 'company' | 'captcha');
    setAnimationLeftToRight(stages[activeStageIndex.value] as 'profile' | 'company' | 'captcha');

    activeStageIndex.value += 1;
  }
}

function prevStage() {
  resetValidation(activeStage.value);

  if (activeStageIndex.value > 0) {
    setAnimationLeftToRight(stages[activeStageIndex.value - 1] as 'profile' | 'company' | 'captcha');
    setAnimationRightToLeft(stages[activeStageIndex.value] as 'profile' | 'company' | 'captcha');

    activeStageIndex.value -= 1;
  }
}

async function onSubmit() {
  if (!await triggerValidation()) {
    return;
  }

  const params: typeof AuthAuthRegisterByEmailRouteRpc.request = {
    recaptchaToken: captchaData.value.token,
    email: profileData.value.email,
    password: profileData.value.password,
    firstName: profileData.value.firstName,
    lastName: profileData.value.lastName,
    phone: profileData.value.phone,
    company: {
      name: companyData.value.name,
      url: companyData.value.url,
      storeUrls: companyData.value.shopUrls,
    },
  };

  await $fetch<typeof AuthAuthRegisterByEmailRouteRpc.response>('/api/rpc/auth/auth/register-by-email', {
    method: 'POST',
    body: params,
  });

  navigateTo('/');
}

function handleWidgetId(widgetId: number) {
  console.log('Widget ID: ', widgetId);
}
function handleErrorCalback() {
  captchaData.value.token = '';
}
function handleExpiredCallback() {
  captchaData.value.token = '';
}
function handleLoadCallback(response: string) {
  captchaData.value.token = response;
}
</script>

<template>
  <div class="h-full flex flex-col items-center justify-center md:max-w-184 lg:max-w-177 md:overflow-x-hidden">
    <form
      ref="stageWrapperRef"
      class="flex flex-col gap-4 lg:gap-9 justify-between items-center md:h-full w-max md:max-h-165 lg:h-max lg:max-h-200 w-100dvw max-w-100dvw lg:max-w-177 px-8 md:px-4 lg:px-0"
      @submit.prevent="onSubmit"
    >
      <div class="max-w-100dvw px-6 md:px-auto">
        <StageIndicator :stage="activeStage" />
      </div>
      <div
        class="w-max md:w-full md:h-max mt-6 transition-height-600 relative flex md:overflow-x-hidden justify-center"
        :style="{ height: stageContainerHeight ? `${stageContainerHeight}px` : 'auto' }"
      >
        <Transition
          enter-active-class="opacity-0 scale-95"
          leave-active-class="opacity-0 scale-95"
          mode="out-in"
        >
          <div
            v-if="activeStage === 'profile'"
            ref="profileStageRef"
            class="transition-opacity,transform-300 h-min md:h-auto"
          >
            <ProfileRegistrationStage
              v-model="profileData"
              :errors="profileErrors"
              class="w-[calc(100dvw-64px)] md:max-w-184 lg:max-w-170"
            />
          </div>
          <div
            v-else-if="activeStage === 'company'"
            ref="companyStageRef"
            class="transition-opacity,transform-300 h-min w-full flex justify-center items-center"
          >
            <CompanyRegistrationStage
              v-model="companyData"
              :errors="companyErrors"
              class="w-[calc(100dvw-64px)] h-min md:h-auto md:max-w-184 lg:max-w-170 md:h-full md:flex md:items-center md:justify-center"
            />
          </div>
          <div
            v-else-if="activeStage === 'captcha'"
            ref="captchaStageRef"
            class="max-w-100dvw w-[calc(100dvw-64px)] md:max-w-184 lg:w-170 flex justify-center items-center lg:h-89"
          >
            <RecaptchaV2
              @widget-id="handleWidgetId"
              @error-callback="handleErrorCalback"
              @expired-callback="handleExpiredCallback"
              @load-callback="handleLoadCallback"
            />
          </div>
        </Transition>
      </div>

      <div class="flex flex-col items-center gap-8 lg:gap-10 max-w-100dvw w-full md:w-81.5 lg:w-177">
        <div v-if="activeStageIndex < stages.length - 1" class="flex items-center justify-center w-full gap-4">
          <BaseButton
            v-if="activeStageIndex > 0"
            type="button"
            size="md"
            variant="secondary"
            label="Previous step"
            class="flex-1 lg:flex-none lg:w-40!"
            @click="prevStage"
          />
          <BaseButton
            type="button"
            size="md"
            variant="primary"
            label="Next step"
            class="flex-1 lg:flex-none lg:w-40!"
            @click="nextStage"
          />
        </div>

        <div v-if="activeStageIndex === stages.length - 1" class="flex items-center justify-center gap-4">
          <BaseButton
            v-if="activeStageIndex > 0"
            type="submit"
            size="md"
            variant="primary"
            class="flex-1 lg:flex-none"
            :disabled="captchaHasErrors"
            label="Create Account"
          />
        </div>

        <div class="flex justify-center">
          <span>Already have an account? <BaseClickableText prefetch to="/login" text="Log In" /></span>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss">
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.hidden-registration-stage * {
  pointer-events: none;
}
</style>
